/**
 * @file Confirme l'empreinte bancaire chez Stripe, ou réutilise une confirmation déjà acquise.
 * @module packages.common.components.table-reservation.utils.confirmCardHold
 *
 * Un intent Stripe ne se confirme qu'une fois. Si la création de la réservation
 * échoue après une carte validée (service devenu complet entre-temps, coupure),
 * un nouveau clic ne doit pas reconfirmer le même intent : Stripe répond alors
 * `setup_intent_unexpected_state` à chaque tentative et le client est bloqué.
 * La confirmation acquise est donc mémorisée avec son `clientSecret` et
 * réutilisée tant que l'intent n'a pas changé.
 */
import {PAYMENT_ERROR_STAGES} from './paymentSignal';

/**
 * Convertit une erreur Stripe.js en Error jetable, en conservant les codes
 * stables (code, decline_code) pour le signal empreinte du brouillon.
 *
 * @param {Object} error - Erreur renvoyée par confirmCardSetup / confirmCardPayment
 * @return {Error} Erreur marquée `isStripeError`
 */
function toStripeError(error) {
  // Stripe fournit des messages déjà traduits dans la langue du navigateur
  const stripeError = new Error(error.message);
  stripeError.isStripeError = true;
  stripeError.stripeCode = error.code;
  stripeError.declineCode = error.decline_code;
  stripeError.paymentStage = PAYMENT_ERROR_STAGES.CONFIRM;
  return stripeError;
}

/**
 * Construit l'erreur d'un intent confirmé mais dans un état inattendu.
 *
 * @param {string} message - Message technique
 * @param {string} status - Statut Stripe de l'intent
 * @return {Error} Erreur portant `stripeCode` = statut et l'étape `confirm`
 */
function toIntentStateError(message, status) {
  const error = new Error(message);
  error.stripeCode = `intent_${status}`;
  error.paymentStage = PAYMENT_ERROR_STAGES.CONFIRM;
  return error;
}

/**
 * Résout le type d'intent à confirmer.
 *
 * Source de vérité = `intentType` retourné par le backend. Les fallbacks
 * protègent les widgets servis depuis un cache navigateur antérieur où
 * `intentType` n'était pas encore renvoyé ; `captureMethod === 'setup_only'`
 * prime sur `approach` pour ne pas retomber sur 'payment' quand les deux
 * sont découplés.
 *
 * @param {Object} paymentConfig - Configuration d'empreinte reçue du backend
 * @return {'setup'|'payment'} Type d'intent
 */
function resolveIntentType(paymentConfig) {
  const captureMethod = paymentConfig?.captureMethod;
  return paymentConfig?.intentType ||
    (captureMethod === 'setup_only' ? 'setup' : null) ||
    (paymentConfig?.approach === 'deferred' ? 'setup' : 'payment');
}

/**
 * Indique si une confirmation déjà acquise vaut encore pour cet intent.
 *
 * Un intent réussi ne peut plus être confirmé, avec aucune carte : si le client
 * retape sa carte après un échec de création, c'est la carte déjà validée qui
 * reste attachée à l'empreinte, et la création est simplement retentée.
 *
 * @param {Object|null} confirmedIntent - Confirmation mémorisée par un clic précédent
 * @param {Object} paymentConfig - Configuration d'empreinte courante
 * @return {boolean} true si l'intent confirmé est bien celui de la configuration courante
 */
export function isIntentAlreadyConfirmed(confirmedIntent, paymentConfig) {
  return !!confirmedIntent?.clientSecret && confirmedIntent.clientSecret === paymentConfig?.clientSecret;
}

/**
 * Confirme la carte chez Stripe pour l'intent courant.
 *
 * @param {Object} stripe - Instance Stripe.js initialisée sur le bon compte
 * @param {Object} paymentConfig - Configuration d'empreinte (clientSecret, intentType…)
 * @param {string} paymentMethodId - PaymentMethod créé par Stripe Elements
 * @return {Promise<Object>} Confirmation `{clientSecret, intentType, intentId, paymentMethodId}`
 * @throws {Error} Erreur Stripe (`isStripeError`) ou d'état inattendu
 */
async function confirmWithStripe(stripe, paymentConfig, paymentMethodId) {
  const intentType = resolveIntentType(paymentConfig);
  const options = {payment_method: paymentMethodId};

  if (intentType === 'setup') {
    // SetupIntent : sauvegarde de la carte sans blocage (setup_only ou preauth deferred)
    const {error, setupIntent} = await stripe.confirmCardSetup(paymentConfig.clientSecret, options);
    if (error) throw toStripeError(error);
    if (setupIntent.status !== 'succeeded') {
      throw toIntentStateError(`Enregistrement de la carte invalide: ${setupIntent.status}`, setupIntent.status);
    }
    // Le SetupIntent attache un PaymentMethod au Customer : c'est celui-là qui compte, pas le standalone
    return {clientSecret: paymentConfig.clientSecret, intentType, intentId: setupIntent.id, paymentMethodId: setupIntent.payment_method};
  }

  // PaymentIntent capture_method=manual : pré-autorisation (blocage d'argent)
  const {error, paymentIntent} = await stripe.confirmCardPayment(paymentConfig.clientSecret, options);
  if (error) throw toStripeError(error);
  if (paymentIntent.status !== 'requires_capture') {
    throw toIntentStateError(`État d'empreinte invalide: ${paymentIntent.status}`, paymentIntent.status);
  }
  return {clientSecret: paymentConfig.clientSecret, intentType, intentId: paymentIntent.id, paymentMethodId};
}

/**
 * Obtient une confirmation d'empreinte : celle déjà acquise si l'intent n'a
 * pas changé, sinon une confirmation Stripe.
 *
 * @param {Object} params - Paramètres
 * @param {Object} params.stripe - Instance Stripe.js
 * @param {Object} params.paymentConfig - Configuration d'empreinte courante
 * @param {string} params.paymentMethodId - PaymentMethod saisi
 * @param {Object|null} params.confirmedIntent - Confirmation mémorisée, ou null
 * @return {Promise<{confirmation: Object, reused: boolean}>} Confirmation et provenance
 */
export async function confirmCardHold({stripe, paymentConfig, paymentMethodId, confirmedIntent}) {
  if (isIntentAlreadyConfirmed(confirmedIntent, paymentConfig)) {
    return {confirmation: confirmedIntent, reused: true};
  }
  const confirmation = await confirmWithStripe(stripe, paymentConfig, paymentMethodId);
  return {confirmation, reused: false};
}

/**
 * Champs d'empreinte à joindre au payload de création de réservation.
 *
 * La source et le montant de l'empreinte sont recalculés côté serveur à partir
 * des snapshots de formule et de la politique globale : aucun champ financier
 * client n'est transmis.
 *
 * @param {Object} confirmation - Confirmation (cf. confirmCardHold)
 * @param {Object} paymentConfig - Configuration d'empreinte courante
 * @return {Object} Champs `setupIntentId`/`paymentIntentId`, `paymentMethodId`, `paymentApproach`, etc.
 */
export function buildCardHoldPayload(confirmation, paymentConfig) {
  const captureMethod = paymentConfig?.captureMethod;
  if (confirmation.intentType === 'setup') {
    return {
      setupIntentId: confirmation.intentId,
      paymentMethodId: confirmation.paymentMethodId,
      paymentApproach: 'deferred',
      stripeCustomerId: paymentConfig?.stripeCustomerId,
      ...(captureMethod && {captureMethod}),
    };
  }
  return {
    paymentIntentId: confirmation.intentId,
    paymentApproach: 'immediate',
    ...(captureMethod && {captureMethod}),
  };
}
